import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Inbox, Users, DollarSign, Download, MessageSquare, Send, Receipt, UserPlus, ShieldCheck, Mail, Phone, ChevronDown, ArrowUpRight, Archive, ArchiveRestore, Trash2, CheckCircle2, LogOut, RotateCcw, LayoutDashboard, CalendarClock, Wallet } from "lucide-react";
import { fetchEnquiries, setEnquiryStatus, deleteEnquiry, fetchClients, fetchFees, saveFees, adminDownloadDocument, adminDocumentPreviewUrl, fetchClientMessages, sendClientMessage, fetchClientActivity, adminSetStatus, adminGetChecklist, adminRequestItem, deleteClient, requestClientInfo, fetchNotifications, formatApiError } from "@/lib/api";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
import { useVisibleInterval } from "@/lib/useVisibleInterval";
import InvoiceTab from "@/components/admin/InvoiceTab";
import { AdminOverview } from "@/components/admin/AdminOverview";
import NewClientTab from "@/components/admin/NewClientTab";
import ClientDetailsTab from "@/components/admin/ClientDetailsTab";
import ClientInvoicesTab from "@/components/admin/ClientInvoicesTab";
import ComplianceTab from "@/components/admin/ComplianceTab";
import SchedulingTab from "@/components/admin/SchedulingTab";
import PayrollTab from "@/components/admin/PayrollTab";
import { SectionHead } from "@/lib/motion";
import { PORTAL_BASICS, INCOME_TYPES, COMPLEXITY_ITEMS, COMPLEXITY_CATEGORIES, COMPLEXITY_TIERS } from "@/data/content";
import { computeFeeTiers } from "@/lib/priceTier";
import TierMeter from "@/components/TierMeter";
import NotificationBell from "@/components/admin/NotificationBell";
import { useAuth } from "@/lib/AuthContext";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hover preview for an uploaded document — lazily fetches the file and renders
// it inline. Images render directly; PDFs are rasterised (first page) with
// PDF.js so the preview always shows, regardless of Chrome's "download PDFs"
// setting that would blank an <iframe>.
function DocPreview({ adminKey, doc, children }) {
  const [imgSrc, setImgSrc] = useState(null); // blob URL (image) or dataURL (pdf page)
  const [pages, setPages] = useState(0);
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const urlRef = useRef(null);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const ct = doc.content_type || "";
  const isImage = ct.startsWith("image/");
  const isPdf = ct === "application/pdf";
  const previewable = isImage || isPdf;

  const onOpenChange = async (open) => {
    if (!open || state !== "idle" || !previewable) return;
    setState("loading");
    try {
      const url = await adminDocumentPreviewUrl(adminKey, doc);
      urlRef.current = url;
      if (isImage) {
        setImgSrc(url);
        setState("ready");
        return;
      }
      const pdf = await pdfjsLib.getDocument(url).promise;
      setPages(pdf.numPages);
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      setImgSrc(canvas.toDataURL("image/png"));
      setState("ready");
      URL.revokeObjectURL(url);
      urlRef.current = null;
    } catch {
      setState("error");
    }
  };

  return (
    <HoverCard openDelay={200} closeDelay={100} onOpenChange={onOpenChange}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side="left"
        align="start"
        data-testid={`admin-doc-preview-${doc.id}`}
        className="w-96 border-steel-border bg-steel-elevated p-3 text-steel-text"
      >
        <p className="mb-2 truncate font-mono text-[10px] uppercase tracking-[0.15em] text-steel-muted">
          {doc.original_filename}
        </p>
        {!previewable ? (
          <div className="flex h-40 items-center justify-center border border-dashed border-steel-border text-center text-xs text-steel-muted">
            No inline preview for this file type. Use download to view.
          </div>
        ) : state === "loading" || state === "idle" ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-steel-brass" />
          </div>
        ) : state === "error" ? (
          <div className="flex h-40 items-center justify-center border border-dashed border-red-500/50 text-center text-xs text-red-400">
            Could not load preview.
          </div>
        ) : (
          <>
            <img
              src={imgSrc}
              alt={doc.original_filename}
              onError={() => setState("error")}
              className="max-h-96 w-full rounded-sm object-contain"
            />
            {isPdf && pages > 1 && (
              <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-steel-muted">
                Page 1 of {pages} — download for the full document
              </p>
            )}
          </>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

const TYPE_LABELS = {
  general: "General",
  "new-client": "New client",
  returning: "Returning",
  estimate: "Estimate",
  consultation: "Consultation",
};

const ENQUIRY_STATUS_META = {
  new: { label: "New", cls: "border-steel-brass/50 text-steel-brass" },
  responded: { label: "Responded", cls: "border-emerald-500/50 text-emerald-400" },
  archived: { label: "Archived", cls: "border-steel-muted/60 text-steel-muted" },
};

const inputCls =
  "w-full border border-steel-border bg-steel-elevated px-4 py-3 text-sm text-steel-text placeholder:text-steel-muted transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none";

const feeInputCls =
  "w-28 border border-steel-border bg-steel-elevated px-3 py-2.5 text-sm text-steel-text transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none";

const PAGE_SIZE = 8;

const FEE_INFO = {
  dependents: ["Dependents", "Charged when the return includes dependents & family credits"],
  college: ["College expenses", "Education credits and 1098-T forms"],
  retirement: ["Retirement income", "Pensions, IRA distributions and SSA-1099"],
  stocks: ["Stock sales", "Capital gains/losses and 1099-B reconciliation"],
  crypto: ["Cryptocurrency", "Crypto transaction reporting"],
  "self-employed": ["Self-employment / 1099", "Schedule C, expenses and estimated taxes"],
  rental: ["Rental property", "Schedule E, depreciation and property sales"],
  k1: ["K-1 income", "Partnership, S-corp and trust income"],
  "multi-state": ["Per state filed", "Each state return filed, in addition to the federal return"],
  "home-sale": ["Home sale", "Sale of a primary residence"],
  royalties: ["Royalty income", "Royalties reported on Schedule E"],
  farm: ["Farm income", "Farm income & expenses on Schedule F"],
  "foreign-income": ["Foreign income", "Foreign earned income exclusion (2555) / FBAR / FATCA"],
  "prior-year": ["Prior-year returns", "Catch-up filings for previous years"],
  "interest-dividends": ["Interest & dividends", "1099-INT / 1099-DIV — savings and brokerage accounts"],
  marketplace: ["Marketplace health insurance", "Form 1095-A & Premium Tax Credit reconciliation"],
  hsa: ["HSA", "Forms 1099-SA / 8889"],
  "dependent-care": ["Child/dependent care expenses", "Daycare, preschool, dependent-care credit"],
  unemployment: ["Unemployment income", "Form 1099-G"],
  gambling: ["Gambling winnings", "Form W-2G reporting"],
  itemized: ["Itemized deductions / homeowner", "Mortgage interest, property taxes, charitable, medical"],
  "student-loan": ["Student loan interest", "Form 1098-E — deduction for interest paid"],
  "ira-deduction": ["IRA contributions", "Traditional IRA contribution deduction"],
  educator: ["Educator expenses", "Out-of-pocket classroom supplies deduction"],
  eitc: ["Earned Income Credit (EITC)", "EITC eligibility & due-diligence review"],
  "saver-credit": ["Saver's Credit", "Retirement Savings Contributions Credit (Form 8880)"],
  "energy-home": ["Home energy credits", "Energy Efficient Home & Residential Clean Energy (Form 5695)"],
  "ev-credit": ["Electric vehicle credit", "Clean Vehicle Credit — new or used EV (Form 8936)"],
  adoption: ["Adoption credit", "Qualified adoption expenses (Form 8839)"],
  "foreign-tax": ["Foreign tax credit", "Credit for foreign taxes paid (Form 1116)"],
  "elderly-disabled": ["Elderly / disabled credit", "Credit for the elderly or the disabled (Schedule R)"],
  amended: ["Amended return", "1040-X corrections to a filed return"],
  notice: ["IRS / state notice", "Notice review and response"],
};

function Pager({ page, total, onPage, testid }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (pages <= 1) return null;
  return (
    <div className="mt-6 flex items-center justify-between" data-testid={testid}>
      <button
        data-testid={`${testid}-prev`}
        onClick={() => onPage(page - 1)}
        disabled={page === 1}
        className="border border-steel-border px-4 py-2 text-xs font-medium text-steel-secondary transition-colors hover:text-steel-text disabled:opacity-40"
      >
        Previous
      </button>
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
        Page {page} / {pages} · {total} total
      </span>
      <button
        data-testid={`${testid}-next`}
        onClick={() => onPage(page + 1)}
        disabled={page === pages}
        className="border border-steel-border px-4 py-2 text-xs font-medium text-steel-secondary transition-colors hover:text-steel-text disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}



const CHECKLIST_LABELS = (() => {
  const map = {};
  PORTAL_BASICS.forEach((t, i) => {
    map[`basic-${i}`] = t;
  });
  INCOME_TYPES.forEach((t) =>
    t.docs.forEach((d, i) => {
      map[`${t.id}-${i}`] = d;
    })
  );
  return map;
})();

const STAGE_OPTIONS = [
  ["gathering", "Gathering documents"],
  ["review", "Under review"],
  ["preparation", "Return in preparation"],
  ["client-review", "Ready for client review"],
  ["filed", "Filed"],
];

export default function Admin() {
  const [key, setKey] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [unlockMode, setUnlockMode] = useState("account");
  const [acct, setAcct] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") === "invoices" ? "invoices" : "overview");
  const [invoiceLaunch, setInvoiceLaunch] = useState(null);
  const [enquiries, setEnquiries] = useState(null);
  const [clients, setClients] = useState(null);
  const [fees, setFees] = useState(null);
  const feeTiers = useMemo(() => {
    if (!fees?.items) return {};
    const withBase = [{ id: "__base__", min: fees.base_min, max: fees.base_max }, ...fees.items];
    return computeFeeTiers(withBase);
  }, [fees]);
  const [savingFees, setSavingFees] = useState(false);
  const [viewClient, setViewClient] = useState(null);
  const [detailTab, setDetailTab] = useState("documents");
  const [threads, setThreads] = useState({});
  const [activities, setActivities] = useState({});
  const [clientChecklists, setClientChecklists] = useState({});
  const [requestText, setRequestText] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [reply, setReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [enqSearch, setEnqSearch] = useState("");
  const [enqType, setEnqType] = useState("all");
  const [enqStatusFilter, setEnqStatusFilter] = useState("active");
  const [enqPage, setEnqPage] = useState(1);
  const [clientSearch, setClientSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [clientPage, setClientPage] = useState(1);
  const [expandedEnquiry, setExpandedEnquiry] = useState(null);
  const [enqActionBusy, setEnqActionBusy] = useState("");
  const [confirmDeleteEnquiry, setConfirmDeleteEnquiry] = useState(null);
  const [clientPrefill, setClientPrefill] = useState(null);
  const [confirmDeleteClient, setConfirmDeleteClient] = useState(false);
  const [requestingInfo, setRequestingInfo] = useState("");
  const [notifs, setNotifs] = useState(null);
  const [deletingClient, setDeletingClient] = useState(false);
  const { user, logout, checkAuth } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const pollClientRef = useRef(null);

  const signOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } finally {
      setKey("");
      setAcct({ email: "", password: "" });
      setUnlocked(false);
      setEnquiries(null);
      setClients(null);
      setFees(null);
      setViewClient(null);
      setThreads({});
      setActivities({});
      setClientChecklists({});
      setClientPrefill(null);
      setTab("overview");
      setSigningOut(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin" && !unlocked) {
      loadEnquiries("")
        .then(() => {
          setUnlocked(true);
          loadNotifs();
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fees power the enquiry estimate + pricing tab — load once on unlock,
  // regardless of whether admin entered via account login or admin key.
  useEffect(() => {
    if (unlocked && !fees) fetchFees().then(setFees).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  useEffect(() => {
    if (!viewClient) {
      pollClientRef.current = null;
      return;
    }
    const uid = viewClient.user_id;
    pollClientRef.current = async () => {
      try {
        const [m, a, cl, ch] = await Promise.all([
          fetchClientMessages(key, uid),
          fetchClientActivity(key, uid),
          fetchClients(key),
          adminGetChecklist(key, uid),
        ]);
        setThreads((prev) => ({ ...prev, [uid]: m.messages }));
        setActivities((prev) => ({ ...prev, [uid]: a.activity }));
        setClients(cl.clients);
        setClientChecklists((prev) => ({ ...prev, [uid]: ch }));
        setViewClient((cur) => (cur ? cl.clients.find((x) => x.user_id === cur.user_id) || cur : cur));
      } catch {}
    };
    return () => {
      pollClientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewClient?.user_id, key]);
  useVisibleInterval(() => pollClientRef.current?.(), 20000);
  useVisibleInterval(() => {
    if (unlocked) loadNotifs();
  }, 45000);

  const loadNotifs = async () => {
    try {
      setNotifs(await fetchNotifications(key));
    } catch {
      /* transient — the next poll will retry */
    }
  };

  const openNotification = async (n) => {
    if (n.enquiry_id) {
      switchTab("enquiries");
      setExpandedEnquiry(n.enquiry_id);
      return;
    }
    if (n.tab === "invoices") {
      switchTab("invoices");
      return;
    }
    if (n.user_id) {
      switchTab("clients");
      const list = clients || (await fetchClients(key)).clients;
      if (!clients) setClients(list);
      const target = (list || []).find((c) => c.user_id === n.user_id);
      if (target) openClientDetail(target);
      else toast.error("That client no longer exists.");
    }
  };

  const onRequestInfo = async (client) => {
    setRequestingInfo(client.user_id);
    try {
      const data = await requestClientInfo(key, client.user_id);
      const fields = (data.missing_fields || []).length;
      const docs = (data.pending_documents || []).length;
      const parts = [
        fields > 0 && `${fields} missing detail${fields === 1 ? "" : "s"}`,
        docs > 0 && `${docs} missing document${docs === 1 ? "" : "s"}`,
      ].filter(Boolean);
      toast.success(`Emailed ${client.email} — ${parts.join(" + ")} listed.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRequestingInfo("");
    }
  };

  const onDeleteClient = async (client) => {
    setDeletingClient(true);
    try {
      const data = await deleteClient(key, client.user_id);
      toast.success(
        data.email_sent
          ? `${data.email} deleted — confirmation email sent.`
          : `${data.email} deleted, but the confirmation email failed: ${data.email_error || "unknown error"}`,
      );
      setConfirmDeleteClient(false);
      setViewClient(null);
      const cl = await fetchClients(key);
      setClients(cl.clients);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingClient(false);
    }
  };

  const loadEnquiries = async (k) => {
    const data = await fetchEnquiries(k);
    setEnquiries(data.enquiries);
  };

  const onEnquiryStatus = async (enq, status) => {
    setEnqActionBusy(`${enq.id}:${status}`);
    try {
      await setEnquiryStatus(key, enq.id, status);
      setEnquiries((list) => list.map((e) => (e.id === enq.id ? { ...e, status } : e)));
      toast.success(status === "responded" ? "Marked as responded." : status === "archived" ? "Archived." : "Moved back to new.");
    } catch {
      toast.error("Could not update that enquiry.");
    } finally {
      setEnqActionBusy("");
    }
  };

  const onDeleteEnquiry = async (enq) => {
    setEnqActionBusy(`${enq.id}:delete`);
    try {
      await deleteEnquiry(key, enq.id);
      setEnquiries((list) => list.filter((e) => e.id !== enq.id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete that enquiry.");
    } finally {
      setEnqActionBusy("");
      setConfirmDeleteEnquiry(null);
    }
  };

  const onConvertToClient = (enq) => {
    setClientPrefill({
      name: enq.name,
      email: enq.email,
      phone: enq.phone,
      notes: enq.message,
      items: enq.situation_items || [],
      states: enq.states || [],
      ts: Date.now(),
    });
    setTab("new-client");
    if (enq.status === "new") onEnquiryStatus(enq, "responded");
  };

  // Preliminary estimate for an enquiry, using the same math as the pricing page.
  const enquiryEstimate = (enq) => {
    if (!fees) return null;
    const ids = enq.situation_items || [];
    const sts = enq.states || [];
    if (ids.length === 0 && sts.length === 0) return null;
    const feeFor = (id) => (fees.items || []).find((f) => f.id === id);
    let min = Number(fees.base_min) || 0;
    let max = Number(fees.base_max) || 0;
    for (const id of ids) {
      const f = feeFor(id);
      if (f) {
        min += Number(f.min) || 0;
        max += Number(f.max) || 0;
      }
    }
    const ms = feeFor("multi-state");
    if (ms) {
      min += sts.length * (Number(ms.min) || 0);
      max += sts.length * (Number(ms.max) || 0);
    }
    return { min, max };
  };
  const labelForItem = (id) => COMPLEXITY_ITEMS.find((i) => i.id === id)?.label || id;

  const unlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loadEnquiries(key);
      setUnlocked(true);
      loadNotifs();
    } catch {
      toast.error("Invalid admin key.");
    } finally {
      setLoading(false);
    }
  };

  const accountLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/login`, acct, { withCredentials: true });
      if (r.data?.mfa_required || r.data?.role !== "admin") {
        toast.error("That account does not have admin access.");
        return;
      }
      await loadEnquiries("");
      setUnlocked(true);
      loadNotifs();
      checkAuth();
    } catch {
      toast.error("Invalid credentials or not an admin account.");
    } finally {
      setLoading(false);
    }
  };

  const switchTab = async (t) => {
    setInvoiceLaunch(null);
    setTab(t);
    if (t === "clients") {
      try {
        const data = await fetchClients(key);
        setClients(data.clients);
      } catch {
        toast.error("Could not load clients.");
      }
    }
    if (t === "fees" && fees === null) {
      try {
        setFees(await fetchFees());
      } catch {
        toast.error("Could not load fees.");
      }
    }
  };

  const openQuickAction = (action) => {
    if (action === "mobile-invoice" || action === "new-invoice") {
      switchTab("invoices");
      setInvoiceLaunch(action === "mobile-invoice" ? "mobile" : "standard");
      return;
    }
    if (action === "new-client") setClientPrefill(null);
    if (action === "clients") {
      setViewClient(null);
      setClientSearch("");
      setClientFilter("all");
      setClientPage(1);
    }
    if (action === "enquiries") {
      setEnqSearch("");
      setEnqType("all");
      setEnqStatusFilter("active");
      setEnqPage(1);
      setExpandedEnquiry(null);
    }
    switchTab(action);
  };

  const onSaveFees = async () => {
    setSavingFees(true);
    try {
      const saved = await saveFees(key, {
        base_min: Number(fees.base_min),
        base_max: Number(fees.base_max),
        items: fees.items.map((i) => ({ id: i.id, min: Number(i.min), max: Number(i.max), complexity: Number(i.complexity) || 1 })),
      });
      setFees({ ...fees, ...saved });
      toast.success("Fees updated — the Complexity Check now uses them.");
    } catch {
      toast.error("Could not save fees.");
    } finally {
      setSavingFees(false);
    }
  };

  const openClientDetail = async (c) => {
    setViewClient(c);
    setDetailTab("details");
    try {
      const [m, a, ch] = await Promise.all([
        fetchClientMessages(key, c.user_id),
        fetchClientActivity(key, c.user_id),
        adminGetChecklist(key, c.user_id),
      ]);
      setThreads((t) => ({ ...t, [c.user_id]: m.messages }));
      setActivities((prev) => ({ ...prev, [c.user_id]: a.activity }));
      setClientChecklists((prev) => ({ ...prev, [c.user_id]: ch }));
    } catch {
      toast.error("Could not load client details.");
    }
  };

  const sendReply = async (userId) => {
    if (!reply.trim()) return;
    setSendingReply(true);
    try {
      await sendClientMessage(key, userId, reply.trim());
      setReply("");
      const m = await fetchClientMessages(key, userId);
      setThreads((t) => ({ ...t, [userId]: m.messages }));
    } catch {
      toast.error("Could not send message.");
    } finally {
      setSendingReply(false);
    }
  };

  const onSetStatus = async (userId, stage) => {
    try {
      await adminSetStatus(key, userId, stage);
      const cl = await fetchClients(key);
      setClients(cl.clients);
      setViewClient((cur) => (cur ? cl.clients.find((x) => x.user_id === cur.user_id) || cur : cur));
      toast.success("Status updated — the client sees it now.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  const onDetailsSaved = async (data) => {
    try {
      const cl = await fetchClients(key);
      setClients(cl.clients);
      setViewClient((cur) => (cur ? cl.clients.find((x) => x.user_id === cur.user_id) || cur : cur));
    } catch {
      // non-fatal: local save already succeeded
    }
  };

  const onRequestItem = async (userId) => {
    if (!requestText.trim()) return;
    setRequesting(true);
    try {
      await adminRequestItem(key, userId, requestText.trim());
      setRequestText("");
      const ch = await adminGetChecklist(key, userId);
      setClientChecklists((prev) => ({ ...prev, [userId]: ch }));
      toast.success("Requested — it's on the client's checklist now.");
    } catch {
      toast.error("Could not request item.");
    } finally {
      setRequesting(false);
    }
  };

  const setFeeItem = (id, field, value) =>
    setFees((f) => ({ ...f, items: f.items.map((i) => (i.id === id ? { ...i, [field]: value } : i)) }));

  const labelForChecklist = (k) => {
    if (k.startsWith("req-")) {
      const req = (((viewClient && clientChecklists[viewClient.user_id]) || {}).requested || []).find(
        (r) => `req-${r.id}` === k
      );
      return req ? req.text : k;
    }
    return CHECKLIST_LABELS[k] || k;
  };

  const filteredEnquiries = (enquiries || []).filter((enq) => {
    if (enqType !== "all" && enq.enquiry_type !== enqType) return false;
    const status = enq.status || "new";
    if (enqStatusFilter === "active" && status === "archived") return false;
    if (["new", "responded", "archived"].includes(enqStatusFilter) && status !== enqStatusFilter) return false;
    if (!enqSearch.trim()) return true;
    const q = enqSearch.toLowerCase();
    return [enq.name, enq.email, enq.message, enq.phone].filter(Boolean).join(" ").toLowerCase().includes(q);
  });
  const pagedEnquiries = filteredEnquiries.slice((enqPage - 1) * PAGE_SIZE, enqPage * PAGE_SIZE);

  const filteredClients = (clients || []).filter((c) => {
    if (clientFilter === "with-docs" && c.documents.length === 0) return false;
    if (clientFilter === "no-docs" && c.documents.length > 0) return false;
    if (!clientSearch.trim()) return true;
    const q = clientSearch.toLowerCase();
    return [c.name, c.email].filter(Boolean).join(" ").toLowerCase().includes(q);
  });
  const pagedClients = filteredClients.slice((clientPage - 1) * PAGE_SIZE, clientPage * PAGE_SIZE);

  return (
    <div data-testid="admin-page" className="pt-20">
      <section className="border-b border-steel-border">
        <div className={`mx-auto max-w-7xl px-6 lg:px-10 ${unlocked ? "py-8 sm:py-10" : "py-24"}`}>
          <SectionHead overline="Admin" title="Back office." />
        </div>
      </section>

      <section className={`mx-auto max-w-7xl px-6 lg:px-10 ${unlocked ? "pb-16 pt-6 sm:pt-8" : "py-16"}`}>
        {!unlocked ? (
          <div className="max-w-md" data-testid="admin-login-form">
            <div className="mb-5 flex border border-steel-border" data-testid="admin-unlock-tabs">
              {[
                ["account", "Account"],
                ["key", "Admin Key"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`admin-unlock-tab-${id}`}
                  onClick={() => setUnlockMode(id)}
                  className={`flex-1 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    unlockMode === id ? "bg-steel-accent text-white" : "text-steel-muted hover:text-steel-text"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {unlockMode === "account" ? (
              <form onSubmit={accountLogin} className="space-y-4">
                <input
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={acct.email}
                  onChange={(e) => setAcct({ ...acct, email: e.target.value })}
                  className={inputCls}
                  placeholder="admin@steeleindustries.com"
                />
                <input
                  data-testid="admin-password-input"
                  type="password"
                  required
                  value={acct.password}
                  onChange={(e) => setAcct({ ...acct, password: e.target.value })}
                  className={inputCls}
                  placeholder="Password"
                />
                <button
                  data-testid="admin-account-login-btn"
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 bg-steel-text px-6 py-3 text-sm font-semibold text-steel-bg transition-colors hover:bg-zinc-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
                </button>
              </form>
            ) : (
              <form onSubmit={unlock} className="flex gap-3">
                <input
                  data-testid="admin-key-input"
                  type="password"
                  required
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  className={inputCls}
                  placeholder="Admin key"
                />
                <button
                  data-testid="admin-load-btn"
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 bg-steel-text px-6 py-3 text-sm font-semibold text-steel-bg transition-colors hover:bg-zinc-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load"}
                </button>
              </form>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
              {[
                ["overview", "Overview", LayoutDashboard],
                ["enquiries", "Enquiries", Inbox],
                ["new-client", "New Client", UserPlus],
                ["clients", "Clients & Documents", Users],
                ["invoices", "Invoices", Receipt],
                ["fees", "Fee Settings", DollarSign],
                ["compliance", "Compliance", ShieldCheck],
                ["scheduling", "Scheduling", CalendarClock],
                ["payroll", "Payroll", Wallet],
              ].map(([id, label, Icon]) => (
                <button
                  key={id}
                  data-testid={`admin-tab-${id}`}
                  onClick={() => switchTab(id)}
                  className={`flex items-center gap-2 border px-3 py-2.5 text-sm font-medium transition-colors duration-300 sm:px-5 ${
                    tab === id
                      ? "border-steel-brass bg-steel-brass/10 text-steel-text"
                      : "border-steel-border text-steel-secondary hover:text-steel-text"
                  }`}
                >
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
              </div>
              <div className="ml-auto flex items-center gap-4">
                <NotificationBell adminKey={key} data={notifs} onRefresh={loadNotifs} onOpen={openNotification} />
                {user?.email && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted" data-testid="admin-signed-in-as">
                    {user.email}
                  </span>
                )}
                <button
                  data-testid="admin-signout-btn"
                  onClick={signOut}
                  disabled={signingOut}
                  className="flex items-center gap-2 border border-steel-border px-5 py-2.5 text-sm font-medium text-steel-secondary transition-colors duration-300 hover:border-red-500/60 hover:text-red-400 disabled:opacity-60"
                >
                  {signingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />} Sign out
                </button>
              </div>
            </div>

            {tab === "overview" && <AdminOverview onAction={openQuickAction} />}

            {tab === "enquiries" && (
              <div className="mt-10" data-testid="admin-enquiry-list">
                {!enquiries || enquiries.length === 0 ? (
                  <div className="flex flex-col items-start gap-4 border border-steel-border bg-steel-surface p-10">
                    <Inbox className="h-6 w-6 text-steel-muted" />
                    <p className="text-sm text-steel-secondary">No enquiries yet.</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-5 flex flex-col gap-3 sm:flex-row">
                      <input
                        data-testid="enquiry-search"
                        value={enqSearch}
                        onChange={(e) => { setEnqSearch(e.target.value); setEnqPage(1); }}
                        className={inputCls}
                        placeholder="Search name, email or message…"
                      />
                      <select
                        data-testid="enquiry-type-filter"
                        value={enqType}
                        onChange={(e) => { setEnqType(e.target.value); setEnqPage(1); }}
                        className={`${inputCls} sm:w-56`}
                      >
                        <option value="all">All types</option>
                        {Object.entries(TYPE_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                      <select
                        data-testid="enquiry-status-filter"
                        value={enqStatusFilter}
                        onChange={(e) => { setEnqStatusFilter(e.target.value); setEnqPage(1); }}
                        className={`${inputCls} sm:w-48`}
                      >
                        <option value="active">Active (hide archived)</option>
                        <option value="all">All statuses</option>
                        <option value="new">New</option>
                        <option value="responded">Responded</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                    <div className="space-y-4">
                    {pagedEnquiries.map((enq) => {
                      const status = enq.status || "new";
                      const meta = ENQUIRY_STATUS_META[status] || ENQUIRY_STATUS_META.new;
                      const isOpen = expandedEnquiry === enq.id;
                      const preview = enq.message
                        ? enq.message.slice(0, 120) + (enq.message.length > 120 ? "…" : "")
                        : enq.situation?.length > 0
                        ? `Situation: ${enq.situation.join(", ")}`
                        : "No message included.";
                      const busy = (suffix) => enqActionBusy === `${enq.id}:${suffix}`;
                      return (
                        <div key={enq.id} data-testid={`admin-enquiry-${enq.id}`} className="border border-steel-border bg-steel-surface">
                          <button
                            type="button"
                            data-testid={`enquiry-toggle-${enq.id}`}
                            onClick={() => setExpandedEnquiry(isOpen ? null : enq.id)}
                            className="flex w-full items-start justify-between gap-4 p-6 text-left transition-colors duration-300 hover:bg-steel-elevated/40"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="border border-steel-brass/50 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-brass">
                                  {TYPE_LABELS[enq.enquiry_type] || enq.enquiry_type}
                                </span>
                                <span className={`border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${meta.cls}`} data-testid={`enquiry-status-badge-${enq.id}`}>
                                  {meta.label}
                                </span>
                                <span className="font-mono text-xs text-steel-muted">{new Date(enq.created_at).toLocaleString()}</span>
                              </div>
                              <h3 className="mt-3 font-display text-lg font-medium text-steel-text">{enq.name}</h3>
                              <p className="mt-1 text-sm text-steel-secondary">
                                {enq.email}
                                {enq.phone ? ` · ${enq.phone}` : ""}
                              </p>
                              {!isOpen && <p className="mt-2 text-xs text-steel-muted">{preview}</p>}
                            </div>
                            <ChevronDown
                              className={`mt-1 h-5 w-5 shrink-0 text-steel-muted transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                            />
                          </button>

                          {isOpen && (
                            <div className="border-t border-steel-border/60 p-6 pt-5" data-testid={`enquiry-detail-${enq.id}`}>
                              {(enq.situation_items?.length > 0 || enq.states?.length > 0) ? (
                                <div className="border border-steel-brass/30 bg-steel-elevated p-4" data-testid={`enquiry-pricing-${enq.id}`}>
                                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-brass">Pricing items selected</p>
                                  <div className="mt-2 flex flex-wrap gap-1.5">
                                    {(enq.situation_items || []).map((id) => (
                                      <span key={id} className="border border-steel-border px-2 py-0.5 font-mono text-[10px] text-steel-secondary">{labelForItem(id)}</span>
                                    ))}
                                    {(enq.states || []).map((s) => (
                                      <span key={s} className="border border-steel-accent/50 px-2 py-0.5 font-mono text-[10px] text-steel-text">{s}</span>
                                    ))}
                                  </div>
                                  {(() => {
                                    const est = enquiryEstimate(enq);
                                    return est ? (
                                      <p className="mt-3 font-mono text-sm text-steel-text" data-testid={`enquiry-estimate-${enq.id}`}>
                                        Est. total:{" "}
                                        <span className="text-steel-brass">
                                          ${est.min.toLocaleString()} – ${est.max.toLocaleString()}
                                        </span>
                                      </p>
                                    ) : (
                                      <p className="mt-3 font-mono text-[10px] text-steel-muted">Loading estimate…</p>
                                    );
                                  })()}
                                </div>
                              ) : enq.situation?.length > 0 ? (
                                <p className="text-xs text-steel-muted">Situation: {enq.situation.join(", ")}</p>
                              ) : null}
                              {enq.message && (
                                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-steel-secondary">{enq.message}</p>
                              )}

                              <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-steel-border/60 pt-5">
                                <a
                                  href={`mailto:${enq.email}?subject=${encodeURIComponent("Re: your enquiry to Steele Industries")}&body=${encodeURIComponent(`Hi ${enq.name},\n\n`)}`}
                                  data-testid={`enquiry-email-${enq.id}`}
                                  className="flex items-center gap-2 border border-steel-border px-4 py-2 text-xs font-medium text-steel-text transition-colors duration-300 hover:border-steel-brass hover:text-steel-brass"
                                >
                                  <Mail className="h-3.5 w-3.5" /> Email
                                </a>
                                {enq.phone && (
                                  <a
                                    href={`tel:${enq.phone}`}
                                    data-testid={`enquiry-call-${enq.id}`}
                                    className="flex items-center gap-2 border border-steel-border px-4 py-2 text-xs font-medium text-steel-text transition-colors duration-300 hover:border-steel-brass hover:text-steel-brass"
                                  >
                                    <Phone className="h-3.5 w-3.5" /> Call
                                  </a>
                                )}
                                <button
                                  type="button"
                                  data-testid={`enquiry-convert-${enq.id}`}
                                  onClick={() => onConvertToClient(enq)}
                                  className="flex items-center gap-2 bg-steel-accent px-4 py-2 text-xs font-semibold text-white transition-colors duration-300 hover:bg-steel-accentHover"
                                >
                                  <ArrowUpRight className="h-3.5 w-3.5" /> Convert to Client
                                </button>
                                {status !== "responded" && (
                                  <button
                                    type="button"
                                    data-testid={`enquiry-mark-responded-${enq.id}`}
                                    onClick={() => onEnquiryStatus(enq, "responded")}
                                    disabled={busy("responded")}
                                    className="flex items-center gap-2 border border-steel-border px-4 py-2 text-xs font-medium text-steel-secondary transition-colors duration-300 hover:border-emerald-500/60 hover:text-emerald-400 disabled:opacity-50"
                                  >
                                    <CheckCircle2 className="h-3.5 w-3.5" /> Mark Responded
                                  </button>
                                )}
                                {status === "responded" && (
                                  <button
                                    type="button"
                                    data-testid={`enquiry-mark-new-${enq.id}`}
                                    onClick={() => onEnquiryStatus(enq, "new")}
                                    disabled={busy("new")}
                                    className="flex items-center gap-2 border border-steel-border px-4 py-2 text-xs font-medium text-steel-secondary transition-colors duration-300 hover:border-steel-brass hover:text-steel-brass disabled:opacity-50"
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" /> Mark as New
                                  </button>
                                )}
                                {status === "archived" ? (
                                  <button
                                    type="button"
                                    data-testid={`enquiry-unarchive-${enq.id}`}
                                    onClick={() => onEnquiryStatus(enq, "new")}
                                    disabled={busy("new")}
                                    className="flex items-center gap-2 border border-steel-border px-4 py-2 text-xs font-medium text-steel-secondary transition-colors duration-300 hover:border-steel-brass hover:text-steel-brass disabled:opacity-50"
                                  >
                                    <ArchiveRestore className="h-3.5 w-3.5" /> Unarchive
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    data-testid={`enquiry-archive-${enq.id}`}
                                    onClick={() => onEnquiryStatus(enq, "archived")}
                                    disabled={busy("archived")}
                                    className="flex items-center gap-2 border border-steel-border px-4 py-2 text-xs font-medium text-steel-secondary transition-colors duration-300 hover:border-steel-muted hover:text-steel-text disabled:opacity-50"
                                  >
                                    <Archive className="h-3.5 w-3.5" /> Archive
                                  </button>
                                )}
                                {confirmDeleteEnquiry === enq.id ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-red-400">Delete for good?</span>
                                    <button
                                      type="button"
                                      data-testid={`enquiry-delete-confirm-${enq.id}`}
                                      onClick={() => onDeleteEnquiry(enq)}
                                      disabled={busy("delete")}
                                      className="border border-red-500/60 px-3 py-2 text-xs font-semibold text-red-400 transition-colors duration-300 hover:bg-red-500/10 disabled:opacity-50"
                                    >
                                      Yes, delete
                                    </button>
                                    <button
                                      type="button"
                                      data-testid={`enquiry-delete-cancel-${enq.id}`}
                                      onClick={() => setConfirmDeleteEnquiry(null)}
                                      className="text-xs text-steel-muted transition-colors duration-300 hover:text-steel-text"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    data-testid={`enquiry-delete-${enq.id}`}
                                    onClick={() => setConfirmDeleteEnquiry(enq.id)}
                                    className="flex items-center gap-2 border border-steel-border px-4 py-2 text-xs font-medium text-steel-muted transition-colors duration-300 hover:border-red-500/60 hover:text-red-400"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" /> Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    </div>
                    {filteredEnquiries.length === 0 && (
                      <p className="text-sm text-steel-muted" data-testid="enquiries-no-results">No enquiries match that search.</p>
                    )}
                    <Pager testid="enquiries-pager" page={enqPage} total={filteredEnquiries.length} onPage={setEnqPage} />
                  </>
                )}
              </div>
            )}

            {tab === "clients" && (
              <div className="mt-10" data-testid="admin-clients-list">
                {clients === null ? (
                  <div className="flex items-center gap-3 text-sm text-steel-muted">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading clients…
                  </div>
                ) : clients.length === 0 ? (
                  <div className="flex flex-col items-start gap-4 border border-steel-border bg-steel-surface p-10">
                    <Users className="h-6 w-6 text-steel-muted" />
                    <p className="text-sm text-steel-secondary">No client accounts yet.</p>
                  </div>
                ) : viewClient ? (
                  <div data-testid="admin-client-detail">
                    <button
                      data-testid="admin-client-back"
                      onClick={() => setViewClient(null)}
                      className="mb-6 font-mono text-xs uppercase tracking-[0.2em] text-steel-muted transition-colors hover:text-steel-text"
                    >
                      ← All clients
                    </button>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display text-2xl font-medium text-steel-text">{viewClient.name || viewClient.email}</h3>
                      <span className="border border-steel-border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
                        {viewClient.auth_provider}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-steel-secondary">{viewClient.email}</p>
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Return status</span>
                      <select
                        data-testid="admin-status-select"
                        value={viewClient.status || "gathering"}
                        onChange={(e) => onSetStatus(viewClient.user_id, e.target.value)}
                        className={`${inputCls} w-auto`}
                      >
                        {STAGE_OPTIONS.map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                      {confirmDeleteClient ? (
                        <div className="flex flex-wrap items-center gap-3 border border-red-500/60 bg-red-950/30 px-4 py-2">
                          <p className="text-xs leading-relaxed text-red-300">
                            Permanently erase this client's profile, documents, messages and checklist, and email
                            them a deletion confirmation? Sent and paid invoices are kept as financial records.
                          </p>
                          <button
                            type="button"
                            data-testid="admin-delete-client-confirm"
                            onClick={() => onDeleteClient(viewClient)}
                            disabled={deletingClient}
                            className="flex items-center gap-2 bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-500 disabled:opacity-50"
                          >
                            {deletingClient ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            {deletingClient ? "Deleting…" : "Yes, delete permanently"}
                          </button>
                          <button
                            type="button"
                            data-testid="admin-delete-client-cancel"
                            onClick={() => setConfirmDeleteClient(false)}
                            disabled={deletingClient}
                            className="px-3 py-2 text-xs text-steel-muted transition-colors hover:text-steel-text disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          data-testid="admin-delete-client"
                          onClick={() => setConfirmDeleteClient(true)}
                          className="ml-auto flex items-center gap-2 border border-steel-border px-4 py-2 text-xs font-medium text-steel-secondary transition-colors duration-300 hover:border-red-500/60 hover:text-red-400"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete client
                        </button>
                      )}
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-client-tabs">
                      {[
                        ["details", "Details"],
                        ["billing", "Invoices & Estimates"],
                        ["documents", `Documents (${viewClient.documents.length})`],
                        ["conversation", "Conversation"],
                        ["activity", "Activity"],
                        ["checklist", "Checklist"],
                      ].map(([id, label]) => (
                        <button
                          key={id}
                          data-testid={`admin-client-tab-${id}`}
                          onClick={() => setDetailTab(id)}
                          className={`border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                            detailTab === id
                              ? "border-steel-brass bg-steel-brass/10 text-steel-text"
                              : "border-steel-border text-steel-secondary hover:text-steel-text"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {detailTab === "details" && (
                      <ClientDetailsTab
                        key={viewClient.user_id}
                        adminKey={key}
                        client={viewClient}
                        onSaved={onDetailsSaved}
                      />
                    )}

                    {detailTab === "billing" && (
                      <ClientInvoicesTab
                        key={viewClient.user_id}
                        adminKey={key}
                        client={viewClient}
                      />
                    )}

                    {detailTab === "documents" && (
                      <div className="mt-6 space-y-2" data-testid="admin-client-documents">
                        {viewClient.documents.length === 0 ? (
                          <p className="border border-dashed border-steel-border p-6 text-sm text-steel-muted">Nothing uploaded yet.</p>
                        ) : (
                          viewClient.documents.map((doc) => (
                            <div key={doc.id} className="flex items-center justify-between gap-4 border border-steel-border/60 bg-steel-elevated px-4 py-3">
                              <DocPreview adminKey={key} doc={doc}>
                                <div className="min-w-0 cursor-zoom-in" data-testid={`admin-doc-hover-${doc.id}`}>
                                  <p className="truncate text-sm text-steel-text">{doc.original_filename}</p>
                                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel-muted">
                                    {new Date(doc.created_at).toLocaleString()}
                                  </p>
                                  {doc.checklist_key && (
                                    <p className="mt-1 font-mono text-[10px] text-steel-brass">
                                      for: {labelForChecklist(doc.checklist_key)}
                                    </p>
                                  )}
                                </div>
                              </DocPreview>
                              <button
                                data-testid={`admin-doc-download-${doc.id}`}
                                onClick={() => adminDownloadDocument(key, doc)}
                                className="flex h-8 w-8 shrink-0 items-center justify-center border border-steel-border text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass"
                                aria-label="Download"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {detailTab === "conversation" && (
                      <div className="mt-6" data-testid={`admin-thread-${viewClient.user_id}`}>
                        <div className="max-h-96 space-y-3 overflow-y-auto pr-1">
                          {(threads[viewClient.user_id] || []).length === 0 ? (
                            <p className="border border-dashed border-steel-border p-6 text-sm text-steel-muted">No messages yet — start the conversation.</p>
                          ) : (
                            (threads[viewClient.user_id] || []).map((m) => (
                              <div
                                key={m.id}
                                className={`max-w-[85%] border px-4 py-3 ${
                                  m.sender === "admin"
                                    ? "ml-auto border-steel-accent/40 bg-steel-accent/10"
                                    : "border-steel-border bg-steel-elevated"
                                }`}
                              >
                                <p className="text-sm text-steel-text">{m.body}</p>
                                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-steel-muted">
                                  {m.sender === "admin" ? "You" : m.sender_name || "Client"} ·{" "}
                                  {new Date(m.created_at).toLocaleString()}
                                </p>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="mt-3 flex gap-2">
                          <input
                            data-testid={`admin-reply-input-${viewClient.user_id}`}
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            className={inputCls}
                            placeholder="Reply to client…"
                            maxLength={2000}
                          />
                          <button
                            data-testid={`admin-reply-send-${viewClient.user_id}`}
                            onClick={() => sendReply(viewClient.user_id)}
                            disabled={sendingReply || !reply.trim()}
                            className="flex shrink-0 items-center gap-2 bg-steel-accent px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-steel-accentHover disabled:opacity-50"
                          >
                            {sendingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    )}

                    {detailTab === "activity" && (
                      <div className="mt-6" data-testid="admin-client-activity">
                        {(activities[viewClient.user_id] || []).length === 0 ? (
                          <p className="border border-dashed border-steel-border p-6 text-sm text-steel-muted">No activity yet.</p>
                        ) : (
                          (activities[viewClient.user_id] || []).map((ev) => (
                            <p key={ev.id} className="border-b border-dotted border-steel-border py-2.5 text-sm text-steel-secondary last:border-0">
                              <span className="font-mono text-xs text-steel-muted">{new Date(ev.created_at).toLocaleString()} — </span>
                              {ev.detail || ev.action}
                            </p>
                          ))
                        )}
                      </div>
                    )}

                    {detailTab === "checklist" && (
                      <div className="mt-6" data-testid="admin-client-checklist">
                        <p className="text-sm leading-relaxed text-steel-secondary">
                          Request additional documents or items — they appear in the client's portal checklist
                          instantly, and their completion is recorded in the activity log.
                        </p>
                        <div className="mt-4 flex gap-2">
                          <input
                            data-testid="admin-request-input"
                            value={requestText}
                            onChange={(e) => setRequestText(e.target.value)}
                            className={inputCls}
                            placeholder="e.g. 1098-T from your college"
                            maxLength={300}
                          />
                          <button
                            data-testid="admin-request-send"
                            onClick={() => onRequestItem(viewClient.user_id)}
                            disabled={requesting || !requestText.trim()}
                            className="flex shrink-0 items-center gap-2 bg-steel-accent px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-steel-accentHover disabled:opacity-50"
                          >
                            {requesting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request"}
                          </button>
                        </div>
                        <div className="mt-5 space-y-2">
                          {((clientChecklists[viewClient.user_id] || {}).requested || []).length === 0 ? (
                            <p className="border border-dashed border-steel-border p-5 text-xs text-steel-muted">No requested items yet.</p>
                          ) : (
                            ((clientChecklists[viewClient.user_id] || {}).requested || []).map((item) => {
                              const done = ((clientChecklists[viewClient.user_id] || {}).checked || {})[`req-${item.id}`];
                              return (
                                <div key={item.id} data-testid={`admin-requested-${item.id}`} className="flex items-center justify-between gap-4 border border-steel-border/60 bg-steel-elevated px-4 py-3">
                                  <span className={`text-sm ${done ? "text-steel-muted line-through" : "text-steel-text"}`}>{item.text}</span>
                                  <span className={`shrink-0 font-mono text-[9px] uppercase tracking-[0.2em] ${done ? "text-steel-brass" : "text-amber-400"}`}>
                                    {done ? "Done" : "Pending"}
                                  </span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="mb-5 flex flex-col gap-3 sm:flex-row">
                      <input
                        data-testid="client-search"
                        value={clientSearch}
                        onChange={(e) => { setClientSearch(e.target.value); setClientPage(1); }}
                        className={inputCls}
                        placeholder="Search clients by name or email…"
                      />
                      <select
                        data-testid="client-filter"
                        value={clientFilter}
                        onChange={(e) => { setClientFilter(e.target.value); setClientPage(1); }}
                        className={`${inputCls} sm:w-56`}
                      >
                        <option value="all">All clients</option>
                        <option value="with-docs">With documents</option>
                        <option value="no-docs">Without documents</option>
                      </select>
                    </div>
                    <div className="space-y-4">
                    {pagedClients.map((c) => (
                      <div key={c.user_id} data-testid={`admin-client-${c.user_id}`} className="flex flex-wrap items-center justify-between gap-4 border border-steel-border bg-steel-surface p-6">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-3">
                            <h3 className="font-display text-lg font-medium text-steel-text">{c.name || c.email}</h3>
                            <span className="border border-steel-border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
                              {c.auth_provider}
                            </span>
                            {!c.details_confirmed && (
                              <span
                                data-testid={`admin-client-awaiting-${c.user_id}`}
                                className="border border-amber-500/50 bg-amber-500/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300"
                              >
                                Awaiting client info
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-steel-secondary">{c.email}</p>
                          <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
                            {c.documents.length} document{c.documents.length === 1 ? "" : "s"} ·{" "}
                            {(STAGE_OPTIONS.find(([v]) => v === (c.status || "gathering")) || [])[1]}
                          </p>
                          {(c.missing_fields || []).length > 0 && (
                            <p className="mt-2 max-w-xl text-xs leading-relaxed text-amber-300/80" data-testid={`admin-client-missing-${c.user_id}`}>
                              Still blank: {c.missing_fields.map((m) => m.label).join(", ")}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-3">
                          {(c.missing_fields || []).length > 0 && (
                            <button
                              data-testid={`admin-request-info-${c.user_id}`}
                              onClick={() => onRequestInfo(c)}
                              disabled={requestingInfo === c.user_id}
                              className="flex items-center gap-2 border border-amber-500/50 px-5 py-2.5 text-xs font-medium text-amber-300 transition-colors hover:bg-amber-500/10 disabled:opacity-50"
                            >
                              {requestingInfo === c.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                              Email request
                            </button>
                          )}
                          <button
                            data-testid={`admin-open-client-${c.user_id}`}
                            onClick={() => openClientDetail(c)}
                            className="flex items-center gap-2 border border-steel-border px-5 py-2.5 text-xs font-medium text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass"
                          >
                            <MessageSquare className="h-3.5 w-3.5" /> Open client
                          </button>
                        </div>
                      </div>
                    ))}
                    </div>
                    {filteredClients.length === 0 && (
                      <p className="text-sm text-steel-muted" data-testid="clients-no-results">No clients match that search.</p>
                    )}
                    <Pager testid="clients-pager" page={clientPage} total={filteredClients.length} onPage={setClientPage} />
                  </>
                )}
              </div>
            )}

            {tab === "invoices" && <InvoiceTab key={invoiceLaunch || "list"} adminKey={key} initialKind={invoiceLaunch} />}

            {tab === "new-client" && (
              <NewClientTab adminKey={key} prefill={clientPrefill} onPrefillApplied={() => setClientPrefill(null)} />
            )}

            {tab === "compliance" && <ComplianceTab adminKey={key} />}
            {tab === "scheduling" && <SchedulingTab adminKey={key} />}
            {tab === "payroll" && <PayrollTab adminKey={key} />}

            {tab === "fees" && (
              <div className="mt-10 max-w-2xl" data-testid="admin-fees-editor">
                {fees === null ? (
                  <div className="flex items-center gap-3 text-sm text-steel-muted">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading fees…
                  </div>
                ) : (
                  <>
                    <p className="text-sm leading-relaxed text-steel-secondary">
                      These ranges power the Complexity Check estimate: base fee + the range for each selected
                      item. The complexity tier shown to clients is the HIGHEST tier among their selected
                      items — not a sum — so set each item's tier below to match how complex it truly makes a
                      return.
                    </p>
                    <div className="mt-8 border border-steel-border bg-steel-surface p-6">
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-brass">Base return (W-2, simple)</p>
                        <TierMeter kind="price" value={feeTiers.__base__} />
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-4">
                        {["base_min", "base_max"].map((field) => (
                          <div key={field}>
                            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
                              {field === "base_min" ? "Min $" : "Max $"}
                            </label>
                            <input
                              data-testid={`fee-input-${field}`}
                              type="number"
                              min="0"
                              value={fees[field]}
                              onChange={(e) => setFees({ ...fees, [field]: e.target.value })}
                              className={inputCls}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    {(() => {
                      const catOf = (id) => COMPLEXITY_ITEMS.find((i) => i.id === id)?.cat || "other";
                      const renderRow = (item) => {
                        const info = FEE_INFO[item.id] || [item.id, ""];
                        return (
                          <div
                            key={item.id}
                            data-testid={`fee-row-${item.id}`}
                            className="flex flex-wrap items-center justify-between gap-4 border border-steel-border bg-steel-surface p-5"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <p className="text-sm font-medium text-steel-text">{info[0]}</p>
                                <TierMeter kind="price" value={feeTiers[item.id]} testid={`fee-tier-${item.id}`} />
                                <TierMeter kind="complexity" value={item.complexity} testid={`fee-cmplx-${item.id}`} />
                              </div>
                              <p className="mt-0.5 text-xs text-steel-muted">{info[1]}</p>
                              {item.id === "multi-state" ? (
                                <p className="mt-1 text-[11px] text-steel-muted">Doesn't affect the complexity tier.</p>
                              ) : (
                                <div className="mt-2">
                                  <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.2em] text-steel-muted">
                                    Complexity tier
                                  </label>
                                  <select
                                    data-testid={`fee-input-${item.id}-complexity`}
                                    value={item.complexity || 1}
                                    onChange={(e) => setFeeItem(item.id, "complexity", Number(e.target.value))}
                                    className="w-44 border border-steel-border bg-steel-elevated px-3 py-2 text-xs text-steel-text transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none"
                                  >
                                    {COMPLEXITY_TIERS.map((t) => (
                                      <option key={t.level} value={t.level}>
                                        {t.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                            <div className="flex shrink-0 items-start gap-3">
                              <div>
                                <p className="mb-1 font-mono text-[9px] uppercase tracking-[0.2em] text-steel-muted">Min $</p>
                                <input
                                  data-testid={`fee-input-${item.id}-min`}
                                  type="number"
                                  min="0"
                                  value={item.min}
                                  onChange={(e) => setFeeItem(item.id, "min", e.target.value)}
                                  className={feeInputCls}
                                />
                              </div>
                              <span className="mt-8 text-steel-muted">–</span>
                              <div>
                                <p className="mb-1 font-mono text-[9px] uppercase tracking-[0.2em] text-steel-muted">Max $</p>
                                <input
                                  data-testid={`fee-input-${item.id}-max`}
                                  type="number"
                                  min="0"
                                  value={item.max}
                                  onChange={(e) => setFeeItem(item.id, "max", e.target.value)}
                                  className={feeInputCls}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      };
                      return (
                        <div className="mt-6 space-y-8">
                          {COMPLEXITY_CATEGORIES.map((cat) => {
                            const rows = fees.items.filter((it) => catOf(it.id) === cat.id);
                            if (rows.length === 0) return null;
                            return (
                              <div key={cat.id} data-testid={`fee-group-${cat.id}`}>
                                <div className="mb-3 border-l-2 border-steel-brass/60 pl-3">
                                  <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-steel-brass">
                                    {cat.label}
                                  </p>
                                  <p className="mt-0.5 text-xs text-steel-muted">{cat.hint}</p>
                                </div>
                                <div className="space-y-3">{rows.map(renderRow)}</div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                    <button
                      data-testid="fees-save-btn"
                      onClick={onSaveFees}
                      disabled={savingFees}
                      className="mt-8 flex items-center gap-2 bg-steel-accent px-8 py-3.5 text-sm font-semibold text-white shadow-[0_0_20px_rgba(168,85,247,0.30)] transition-[background-color,box-shadow] duration-300 hover:bg-steel-accentHover disabled:opacity-60"
                    >
                      {savingFees ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Fee Settings"}
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
